// A hold on the app's self-update reloads, for screens where a reload would
// throw away typed work.
//
// The service-worker handshake in src/main.tsx reloads the page at moments the
// user is not looking — when the tab goes hidden, and (for iOS PWAs, which
// freeze instead of running the hidden reload) when they come back. That is
// right for browsing, and wrong for a half-filled registration packet or an
// unposted daily update: leaving to check something and returning would lose
// the lot.
//
// A held update is not cancelled, only deferred. Releasing the last hold
// re-checks whether an update was waiting, so the swap happens as soon as the
// work is done rather than never.
//
// Counted, not a boolean: two forms can be open at once, and each releases its
// own hold without cutting the other's short.

let holds = 0;
const listeners = new Set<() => void>();

/** True while any screen has asked to postpone update reloads. */
export function areUpdatesHeld(): boolean {
  return holds > 0;
}

/** Runs when the last hold is released — main.tsx uses it to apply a deferred update. */
export function onUpdatesReleased(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Postpones update reloads until the returned function is called.
 * Releasing twice is a no-op, so a double-invoked React cleanup cannot
 * decrement someone else's hold.
 */
export function holdAppUpdates(): () => void {
  holds += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holds -= 1;
    if (holds === 0) listeners.forEach((fn) => fn());
  };
}
